/**
 * Single source of truth for the timezone every timestamp in the app is
 * rendered in.
 *
 * Resolution order (first valid wins):
 *   1. the zone picked in the header profile dropdown (persisted per browser)
 *   2. the `timezone` on the signed-in user's profile (from the auth store)
 *   3. the device timezone reported by the browser
 *
 * A raw `date.toLocaleString()` silently renders in device time and ignores
 * both the profile setting and the dropdown, so every timestamp must go
 * through the formatters here (or `utils/formatDate.ts`, which delegates to
 * them).
 */
import { useAuthStore } from '../store/authStore.js'

/** Sentinel stored when the user wants their profile/device zone to win. */
export const AUTO_TIMEZONE = 'auto'

export const TIMEZONE_OPTIONS: { label: string; value: string }[] = [
  { label: 'Automatic (my timezone)', value: AUTO_TIMEZONE },
  { label: 'UTC', value: 'UTC' },
  { label: 'Europe / London', value: 'Europe/London' },
  { label: 'Europe / Dublin', value: 'Europe/Dublin' },
  { label: 'Europe / Lisbon', value: 'Europe/Lisbon' },
  { label: 'Europe / Madrid', value: 'Europe/Madrid' },
  { label: 'Europe / Paris', value: 'Europe/Paris' },
  { label: 'Europe / Berlin', value: 'Europe/Berlin' },
  { label: 'Europe / Amsterdam', value: 'Europe/Amsterdam' },
  { label: 'Europe / Warsaw', value: 'Europe/Warsaw' },
  { label: 'Europe / Athens', value: 'Europe/Athens' },
  { label: 'Europe / Istanbul', value: 'Europe/Istanbul' },
  { label: 'Europe / Moscow', value: 'Europe/Moscow' },
  { label: 'Africa / Lagos', value: 'Africa/Lagos' },
  { label: 'Africa / Cairo', value: 'Africa/Cairo' },
  { label: 'Africa / Johannesburg', value: 'Africa/Johannesburg' },
  { label: 'Africa / Nairobi', value: 'Africa/Nairobi' },
  { label: 'Asia / Jerusalem', value: 'Asia/Jerusalem' },
  { label: 'Asia / Dubai', value: 'Asia/Dubai' },
  { label: 'Asia / Karachi', value: 'Asia/Karachi' },
  // India has a single zone; Delhi/Mumbai/Chennai all map to Asia/Kolkata.
  { label: 'Asia / Kolkata (IST)', value: 'Asia/Kolkata' },
  { label: 'Asia / Dhaka', value: 'Asia/Dhaka' },
  { label: 'Asia / Bangkok', value: 'Asia/Bangkok' },
  { label: 'Asia / Singapore', value: 'Asia/Singapore' },
  { label: 'Asia / Hong Kong', value: 'Asia/Hong_Kong' },
  { label: 'Asia / Shanghai', value: 'Asia/Shanghai' },
  { label: 'Asia / Tokyo', value: 'Asia/Tokyo' },
  { label: 'Asia / Seoul', value: 'Asia/Seoul' },
  { label: 'Australia / Perth', value: 'Australia/Perth' },
  { label: 'Australia / Sydney', value: 'Australia/Sydney' },
  { label: 'Pacific / Auckland', value: 'Pacific/Auckland' },
  { label: 'America / Sao Paulo', value: 'America/Sao_Paulo' },
  { label: 'America / Bogota', value: 'America/Bogota' },
  { label: 'America / Mexico City', value: 'America/Mexico_City' },
  { label: 'America / New York', value: 'America/New_York' },
  { label: 'America / Chicago', value: 'America/Chicago' },
  { label: 'America / Denver', value: 'America/Denver' },
  { label: 'America / Phoenix', value: 'America/Phoenix' },
  { label: 'America / Los Angeles', value: 'America/Los_Angeles' },
  { label: 'America / Anchorage', value: 'America/Anchorage' },
  { label: 'Pacific / Honolulu', value: 'Pacific/Honolulu' },
]

const STORAGE_KEY = 'kc3-timezone'
/** Fired on the window so open views re-render without a page reload. */
const CHANGE_EVENT = 'kc3:timezone-change'

const validityCache = new Map<string, boolean>()

/** True when the runtime's Intl data actually knows this zone. */
export function isValidTimezone(tz: string | null | undefined): tz is string {
  if (!tz || tz === AUTO_TIMEZONE) return false
  const cached = validityCache.get(tz)
  if (cached !== undefined) return cached
  let valid = true
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz }).format(new Date(0))
  } catch {
    valid = false
  }
  validityCache.set(tz, valid)
  return valid
}

/** The browser's own zone, or UTC when the runtime won't tell us. */
export function getDeviceTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

/** The zone on the signed-in user's profile, if the session carries one. */
export function getProfileTimezone(): string | null {
  type ProfileZones = { timezone?: string | null; tenantTimezone?: string | null }
  let user: ProfileZones | null
  try {
    user = useAuthStore.getState().user as ProfileZones | null
  } catch {
    return null
  }
  const tz = user?.timezone ?? user?.tenantTimezone ?? null
  return isValidTimezone(tz) ? tz : null
}

/**
 * The raw preference as stored — `AUTO_TIMEZONE` or an IANA zone. This is what
 * the dropdown binds to; use {@link getSelectedTimezone} for formatting.
 */
export function getStoredTimezone(): string {
  if (typeof window === 'undefined') return AUTO_TIMEZONE
  let stored: string | null = null
  try {
    stored = localStorage.getItem(STORAGE_KEY)
  } catch {
    return AUTO_TIMEZONE
  }
  // A zone that a previous build wrote but this runtime doesn't know (or a
  // value removed from the option list) must not wedge the whole app into a
  // silent fallback — treat it as "not chosen".
  return isValidTimezone(stored) ? stored : AUTO_TIMEZONE
}

/**
 * The IANA zone every formatter should render in — an explicit choice, else
 * the user's profile zone, else the device zone. Always a valid zone.
 */
export function getSelectedTimezone(): string {
  const stored = getStoredTimezone()
  if (stored !== AUTO_TIMEZONE) return stored
  return getProfileTimezone() ?? getDeviceTimezone()
}

/** Persist the dropdown choice and notify every open view. */
export function setSelectedTimezone(tz: string): void {
  if (typeof window === 'undefined') return
  const next = tz === AUTO_TIMEZONE || !isValidTimezone(tz) ? AUTO_TIMEZONE : tz
  try {
    if (next === AUTO_TIMEZONE) localStorage.removeItem(STORAGE_KEY)
    else localStorage.setItem(STORAGE_KEY, next)
  } catch {
    // Private-mode / quota failures still get the in-memory notify below so
    // the current tab reflects the choice.
  }
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT))
}

/**
 * Subscribe to timezone changes — both from this tab (the dropdown) and from
 * another tab (the `storage` event). Returns an unsubscribe function.
 */
export function subscribeTimezone(listener: () => void): () => void {
  if (typeof window === 'undefined') return () => {}
  const onStorage = (e: StorageEvent): void => {
    if (e.key === null || e.key === STORAGE_KEY) listener()
  }
  window.addEventListener(CHANGE_EVENT, listener)
  window.addEventListener('storage', onStorage)
  return () => {
    window.removeEventListener(CHANGE_EVENT, listener)
    window.removeEventListener('storage', onStorage)
  }
}

/** Human label for a zone, falling back to the raw id for custom values. */
export function timezoneLabel(tz: string): string {
  const match = TIMEZONE_OPTIONS.find((o) => o.value === tz)
  if (match) return match.label
  return tz
}

/** The current UTC offset of a zone, e.g. `GMT+5:30`. */
export function timezoneOffsetLabel(tz: string = getSelectedTimezone()): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'shortOffset',
    }).formatToParts(new Date())
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? ''
  } catch {
    return ''
  }
}

/**
 * Parses whatever the API handed us into an instant.
 *
 * Postgres `timestamp` columns hold UTC wall-clock digits with no timezone
 * marker, and the raw-SQL read path leaks them as `"2026-07-02 04:35:53.517"`
 * (see `packages/db/src/timestamps.ts`). `new Date()` reads a bare string like
 * that as *device-local* time, so a row written a minute ago renders hours off
 * — and shifted by a different amount for every viewer. Treat a marker-less
 * timestamp as UTC, matching how it was written.
 */
export function parseTimestamp(
  input: string | number | Date | null | undefined,
): Date | null {
  if (input === null || input === undefined || input === '') return null
  if (input instanceof Date) return Number.isNaN(input.getTime()) ? null : input
  if (typeof input === 'string') {
    const trimmed = input.trim()
    // Date-only ("2026-07-02") is parsed as UTC midnight by spec — but that
    // then renders as the *previous* day for anyone west of UTC, so pin it to
    // local midnight and keep the calendar day intact.
    const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed)
    if (dateOnly) {
      return new Date(
        Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]),
      )
    }
    // No `Z` and no `+HH:MM` offset → UTC wall-clock digits from Postgres.
    const hasZone = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(trimmed)
    const iso = hasZone ? trimmed : `${trimmed.replace(' ', 'T')}Z`
    const d = new Date(iso)
    if (!Number.isNaN(d.getTime())) return d
    // Anything else (locale strings, odd formats) — let the engine try.
    const fallback = new Date(trimmed)
    return Number.isNaN(fallback.getTime()) ? null : fallback
  }
  const d = new Date(input)
  return Number.isNaN(d.getTime()) ? null : d
}

/**
 * `YYYY-MM-DD` for a value that means a *calendar day*, not an instant —
 * date-of-birth, joining date, a date-picker bound.
 *
 * Date pickers emit local midnight, so `toISOString().split('T')[0]` shifts
 * the day backwards for every user east of UTC (a birthday entered as the 13th
 * saves as the 12th in IST). Read the local calendar fields instead.
 */
export function toCalendarDateString(
  value: Date | string | null | undefined,
): string | null {
  if (value === null || value === undefined || value === '') return null
  if (typeof value === 'string') {
    const leading = /^(\d{4}-\d{2}-\d{2})/.exec(value.trim())
    if (leading) return leading[1]!
  }
  const d = value instanceof Date ? value : parseTimestamp(value)
  if (!d || Number.isNaN(d.getTime())) return null
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/**
 * Epoch milliseconds for a timestamp, `NaN` when it can't be parsed — a
 * drop-in for `new Date(x).getTime()` that doesn't misread the marker-less
 * timestamps the raw-SQL read path returns. Use this for every "x ago" /
 * countdown calculation; getting it wrong shifts durations by the viewer's
 * UTC offset rather than just relabelling them.
 */
export function timestampMs(input: string | number | Date | null | undefined): number {
  return parseTimestamp(input)?.getTime() ?? NaN
}

function toDate(input: string | number | Date | null | undefined): Date | null {
  return parseTimestamp(input)
}

/**
 * Core formatter — every other helper here (and in `utils/formatDate.ts`)
 * funnels through this so there is exactly one place that decides the zone.
 */
export function formatInTimezone(
  input: string | number | Date | null | undefined,
  options: Intl.DateTimeFormatOptions,
  locale?: string | string[] | undefined,
  fallback = '',
): string {
  const date = toDate(input)
  if (!date) return fallback
  const tz = getSelectedTimezone()
  try {
    return new Intl.DateTimeFormat(locale, { ...options, timeZone: tz }).format(date)
  } catch {
    // Never let an unexpected zone blank out a timestamp.
    return new Intl.DateTimeFormat(locale, options).format(date)
  }
}

const DATE_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: true,
}

const TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: true,
}

/** Date + time, seconds included (`Jun 01, 2026, 09:30:12 AM`). */
export function formatDateTime(input: string | number | Date | null | undefined): string {
  return formatInTimezone(input, DATE_TIME_OPTIONS, 'en-US')
}

/** Time only, seconds included (`09:30:12 AM`). */
export function formatTime(input: string | number | Date | null | undefined): string {
  return formatInTimezone(input, TIME_OPTIONS, 'en-US')
}

/** Time only, no seconds (`09:30 AM`) — chat bubbles, list rows. */
export function formatClockTime(input: string | number | Date | null | undefined): string {
  return formatInTimezone(input, { hour: '2-digit', minute: '2-digit' }, undefined)
}

/** Calendar date only (`Jun 1, 2026`). */
export function formatDay(input: string | number | Date | null | undefined): string {
  return formatInTimezone(input, { year: 'numeric', month: 'short', day: 'numeric' }, undefined)
}

/** Day + month, no year (`01 Jun`) — chat list separators. */
export function formatDayMonth(input: string | number | Date | null | undefined): string {
  return formatInTimezone(input, { day: '2-digit', month: 'short' }, undefined)
}

/**
 * The calendar day a timestamp falls on *in the selected timezone*, as
 * `YYYY-MM-DD`. Use this for day-grouping and same-day checks — comparing
 * `getDate()` compares device-local days and mis-groups messages near
 * midnight once the selected zone differs from the device.
 */
export function timezoneDayKey(input: string | number | Date | null | undefined): string {
  return formatInTimezone(input, { year: 'numeric', month: '2-digit', day: '2-digit' }, 'en-CA')
}
